import React, { useState, useCallback, useRef } from "react";
import axios from "axios";
import { Upload, ShieldCheck, ShieldX, FileText, CheckCircle, XCircle, AlertTriangle, User, Calendar, Hash } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyPage() {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files[0] || e.target.files[0];
    if (f) { setFile(f); setResult(null); setError(null); }
  }, []);

  const handleVerify = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/verify`, formData);
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleString("pt-BR"); }
    catch { return d; }
  };

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Verificar Assinatura
        </h1>
        <p className="text-gray-500 text-sm mt-1">Valide a autenticidade de um documento assinado digitalmente</p>
      </div>

      {/* Upload */}
      <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 mb-5" data-testid="verify-upload">
        <div
          data-testid="verify-dropzone"
          onDrop={handleFileDrop}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${
            dragging ? "border-[#163A24] bg-[#163A24]/5" : file ? "border-[#8FBC8F] bg-green-50/30" : "border-gray-200 hover:border-[#163A24]/40 hover:bg-gray-50"
          }`}
        >
          <input ref={fileRef} type="file" className="hidden" onChange={handleFileDrop} data-testid="verify-file-input" />
          {file ? (
            <>
              <FileText size={32} className="text-[#163A24] mb-3" />
              <p className="font-semibold text-[#163A24]">{file.name}</p>
              <p className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
              <button className="mt-2 text-xs text-gray-400 underline" onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }}>
                Remover
              </button>
            </>
          ) : (
            <>
              <ShieldCheck size={32} className="text-gray-300 mb-3" />
              <p className="font-medium text-gray-600">Arraste o arquivo assinado aqui</p>
              <p className="text-xs text-gray-400 mt-1">Formatos: .p7, .p7s, .p7m, .xml, .json</p>
            </>
          )}
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="verify-error">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          data-testid="btn-verify"
          onClick={handleVerify}
          disabled={!file || loading}
          className="w-full mt-4 flex items-center justify-center gap-2 bg-[#163A24] text-white py-3 rounded-md font-semibold text-sm hover:bg-[#205234] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
        >
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Verificando...</>
          ) : (
            <><ShieldCheck size={16} /> Verificar Assinatura</>
          )}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div
          data-testid="verify-result"
          className={`bg-white rounded-xl border shadow-sm overflow-hidden ${result.valid ? "border-green-200" : "border-red-200"}`}
        >
          {/* Result header */}
          <div className={`p-5 flex items-center gap-4 ${result.valid ? "bg-green-50" : "bg-red-50"}`}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${result.valid ? "bg-green-100" : "bg-red-100"}`}>
              {result.valid ? <ShieldCheck size={24} className="text-green-600" /> : <ShieldX size={24} className="text-red-600" />}
            </div>
            <div>
              <h3 className={`text-lg font-bold ${result.valid ? "text-green-800" : "text-red-800"}`} style={{ fontFamily: "Outfit, sans-serif" }} data-testid="verify-status">
                {result.valid ? "Assinatura Válida" : "Assinatura Inválida"}
              </h3>
              {result.error && <p className="text-sm text-red-600">{result.error}</p>}
              {result.valid && <p className="text-xs text-green-600">Documento íntegro · Assinatura verificada</p>}
            </div>
          </div>

          {/* Details */}
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 gap-3 text-sm">
              {result.signer_cn && (
                <div className="flex items-start gap-3 p-3 bg-[#FAF9F6] rounded-lg">
                  <User size={16} className="text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Assinante</p>
                    <p className="font-semibold text-gray-800" data-testid="verify-signer">{result.signer_cn.split(":")[0]}</p>
                    {result.signer_cn.includes(":") && <p className="text-xs text-gray-500">CPF: {result.signer_cn.split(":")[1]}</p>}
                    {result.signer_org && <p className="text-xs text-gray-500">{result.signer_org}</p>}
                  </div>
                </div>
              )}

              {result.signing_time && (
                <div className="flex items-start gap-3 p-3 bg-[#FAF9F6] rounded-lg">
                  <Calendar size={16} className="text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Data/Hora da Assinatura</p>
                    <p className="font-medium text-gray-800">{formatDate(result.signing_time)}</p>
                  </div>
                </div>
              )}

              {result.sign_type && (
                <div className="flex items-center gap-3 p-3 bg-[#FAF9F6] rounded-lg">
                  <ShieldCheck size={16} className="text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Padrão de Assinatura</p>
                    <p className="font-semibold text-[#163A24]">{result.sign_type}</p>
                  </div>
                </div>
              )}

              {result.document_hash && (
                <div className="flex items-start gap-3 p-3 bg-[#FAF9F6] rounded-lg">
                  <Hash size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="overflow-hidden">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Hash SHA-256 do Documento</p>
                    <p className="font-mono text-xs text-gray-600 break-all">{result.document_hash}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Validation details */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Detalhes da Validação</p>
              <div className="space-y-2">
                <ValidationItem label="Integridade do documento (hash)" ok={result.hash_valid} />
                <ValidationItem label="Assinatura RSA do certificado" ok={result.signature_valid} />
                <ValidationItem label="Certificado dentro da validade" ok={result.certificate_valid} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ValidationItem({ label, ok }) {
  if (ok === undefined || ok === null) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle size={14} className="text-green-500" /> : <XCircle size={14} className="text-red-500" />}
      <span className={ok ? "text-gray-700" : "text-red-600"}>{label}</span>
    </div>
  );
}
